import React, { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, AreaChart, Area, XAxis, YAxis, BarChart, Bar } from 'recharts';
import { Field, Select, Card, CalcBtn, HeroResult, StatsGrid, InfoBox, DataTable, fmt, fmtD } from '../components/UI';
import { InArticleAd, PostResultAd } from '../components/AdSense';

// ─────────────────────────────────────────────────────────────
// EMI CALCULATOR (also used for Home Loan, Car Loan, Business)
// ─────────────────────────────────────────────────────────────
export function EMICalculator({ defaultP = '500000', defaultR = '8.5', defaultY = '5', color = 'var(--blue)' }) {
  const [p, setP] = useState(defaultP);
  const [r, setR] = useState(defaultR);
  const [y, setY] = useState(defaultY);
  const [type, setType] = useState('years');
  const [res, setRes] = useState(null);

  const calc = () => {
    const P = +p, rate = +r;
    let n = +y;
    if (type === 'years') n *= 12;
    const mr = rate / 12 / 100;
    if (!P || !rate || !n) return;
    const pw = Math.pow(1 + mr, n);
    const emi = P * mr * pw / (pw - 1);
    setRes({ emi, principal: P, interest: emi * n - P, total: emi * n });
  };

  return (
    <div>
      <Card>
        <Field label="Loan Amount" value={p} onChange={setP} prefix="₹" placeholder="5,00,000" />
        <div 
        // style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}
         style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px'}}
        >
          <Field label="Interest Rate" value={r} onChange={setR} suffix="% p.a." step="0.1" />
          <Select label="Tenure Type" value={type} onChange={setType} options={[{ value: 'years', label: 'Years' }, { value: 'months', label: 'Months' }]} />
        <Field label={type === 'years' ? 'Tenure (Years)' : 'Tenure (Months)'} value={y} onChange={setY} placeholder={type === 'years' ? '5' : '60'} />
        </div>
        <div  style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', }}>
        </div>
        <CalcBtn label="Calculate EMI" onClick={calc} color={color} />
      </Card>

      {res && (
        <>
          {/* ── AD: In-article between input and result ── */}
          <InArticleAd />
          <HeroResult label="Monthly EMI" value={fmt(res.emi)} color={color} />
          <StatsGrid chips={[
            { label: 'Principal Amount', value: fmt(res.principal), color: 'var(--green)' },
            { label: 'Total Interest', value: fmt(res.interest), color: 'var(--danger)' },
            { label: 'Total Amount', value: fmt(res.total), color },
            { label: 'Interest Ratio', value: `${(res.interest / res.principal * 100).toFixed(1)}%`, color: 'var(--warn)' },
          ]} />
          <Card style={{ height: '250px', marginBottom: '14px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={[{ name: 'Principal', value: res.principal }, { name: 'Interest', value: res.interest }]}
                  cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                  <Cell fill="var(--green)" />
                  <Cell fill="var(--danger)" />
                </Pie>
                <Tooltip formatter={v => fmt(v)} contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--t1)' }} />
                <Legend formatter={v => <span style={{ color: 'var(--t2)', fontSize: '13px' }}>{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// HOME LOAN-SMART PREPAYMENT CALCULATOR (also used for Home Loan, Car Loan, Business)
// ─────────────────────────────────────────────────────────────
export function HomeLoanCalculator() {
  const [loanAmount, setLoanAmount] = useState("5000000");
  const [interestRate, setInterestRate] = useState("7.7");
  const [tenureYears, setTenureYears] = useState("19");
  const [tenureType, setTenureType] = useState("years");
  const [extraEmiPerYear, setExtraEmiPerYear] = useState("3");
  const [yearlyEmiIncrease, setYearlyEmiIncrease] = useState("5");

  const [result, setResult] = useState(null);

  const calculateEMI = (principal, annualRate, months) => {
    if (annualRate === 0) {
      return principal / months;
    }

    const monthlyRate = annualRate / 100 / 12;

    return (
      (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1)
    );
  };

  const calculateNormalLoan = (loanAmount, interestRate, tenureMonths) => {
    const totalMonths = tenureMonths;

    const emi = calculateEMI(loanAmount, interestRate, totalMonths);

    const monthlyRate = interestRate / 100 / 12;

    let balance = loanAmount;
    let totalInterest = 0;
    let totalPayment = 0;
    let month = 0;

    while (balance > 0 && month < totalMonths) {
      month++;

      const interest = balance * monthlyRate;

      let principal = emi - interest;
      let payment = emi;

      if (principal > balance) {
        principal = balance;
        payment = principal + interest;
      }

      balance -= principal;

      totalInterest += interest;
      totalPayment += payment;
    }

    return {
      emi,
      months: month,
      totalInterest,
      totalPayment,
      balance: Math.max(0, balance),
    };
  };

  const calculateWithStrategy = (
    loanAmount,
    interestRate,
    // tenureYears,
    tenureMonths,
    extraEmiPerYear,
    yearlyEmiIncrease,
  ) => {
    const totalMonths = tenureMonths;

    let currentEMI = calculateEMI(loanAmount, interestRate, totalMonths);

    const initialEMI = currentEMI;

    const monthlyRate = interestRate / 100 / 12;

    let balance = loanAmount;

    let totalInterest = 0;
    let totalPayment = 0;
    let month = 0;

    const yearlyData = [];

    let year = 1;

    let yearInterest = 0;
    let yearPrincipal = 0;
    let yearPayment = 0;
    let yearExtraPayment = 0;

    while (balance > 0 && month < 1000) {
      month++;

      // New year
      if (month > 1 && (month - 1) % 12 === 0) {
        year++;

        currentEMI = currentEMI * (1 + yearlyEmiIncrease / 100);
      }

      // Monthly interest
      const interest = balance * monthlyRate;

      // Regular EMI
      const regularPayment = Math.min(currentEMI, balance + interest);

      const principal = regularPayment - interest;

      balance -= principal;

      totalInterest += interest;
      totalPayment += regularPayment;

      yearInterest += interest;
      yearPrincipal += principal;
      yearPayment += regularPayment;

      // Extra EMI
      if (extraEmiPerYear > 0) {
        const interval = Math.floor(12 / extraEmiPerYear);

        if (interval > 0 && month % interval === 0 && balance > 0) {
          const extraPayment = Math.min(currentEMI, balance);

          balance -= extraPayment;

          totalPayment += extraPayment;

          yearExtraPayment += extraPayment;

          yearPrincipal += extraPayment;

          yearPayment += extraPayment;
        }
      }

      // Year end
      if (month % 12 === 0 || balance <= 0) {
        yearlyData.push({
          year,
          emi: currentEMI,
          interest: yearInterest,
          principal: yearPrincipal,
          extraPayment: yearExtraPayment,
          payment: yearPayment,
          closingBalance: Math.max(0, balance),
        });

        yearInterest = 0;
        yearPrincipal = 0;
        yearPayment = 0;
        yearExtraPayment = 0;
      }

      if (balance <= 0) {
        break;
      }
    }

    return {
      initialEMI,
      finalEMI: currentEMI,
      months: month,
      totalInterest,
      totalPayment,
      yearlyData,
    };
  };

  const formatDuration = (months) => {
    const years = Math.floor(months / 12);

    const remainingMonths = months % 12;

    const result = [];

    if (years > 0) {
      result.push(`${years} year${years > 1 ? "s" : ""}`);
    }

    if (remainingMonths > 0) {
      result.push(`${remainingMonths} month${remainingMonths > 1 ? "s" : ""}`);
    }

    return result.join(" ");
  };

  const calculate = () => {
    const P = Number(loanAmount);
    const R = Number(interestRate);
    const Y = Number(tenureYears);
    const extra = Number(extraEmiPerYear);
    const increase = Number(yearlyEmiIncrease);

    if (!P || !Y || R < 0) {
      return;
    }

    // Convert tenure to months
    const tenureMonths = tenureType === "years" ? Y * 12 : Y;

    const normal = calculateNormalLoan(P, R, tenureMonths);

    const strategy = calculateWithStrategy(P, R, tenureMonths, extra, increase);

    const interestSaved = normal.totalInterest - strategy.totalInterest;

    const monthsSaved = normal.months - strategy.months;

    setResult({
      normal,
      strategy,
      interestSaved,
      monthsSaved,
    });
  };

  return (
    <div>
      <Card>
        {/* Loan Amount - Full Width */}
        <Field
          label="Loan Amount"
          value={loanAmount}
          onChange={setLoanAmount}
          prefix="₹"
        />

        {/* Interest Rate + Tenure Type + Tenure */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "12px",
          }}
        >
          <Field
            label="Interest Rate"
            value={interestRate}
            onChange={setInterestRate}
            suffix="% p.a."
            step="0.1"
          />

          <Select
            label="Tenure Type"
            value={tenureType}
            onChange={setTenureType}
            options={[
              {
                value: "years",
                label: "Years",
              },
              {
                value: "months",
                label: "Months",
              },
            ]}
          />

          <Field
            label={
              tenureType === "years" ? "Tenure (Years)" : "Tenure (Months)"
            }
            value={tenureYears}
            onChange={setTenureYears}
            suffix={tenureType === "years" ? "Years" : "Months"}
            placeholder={tenureType === "years" ? "19" : "228"}
          />
        </div>

        {/* Extra EMI Fields */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "12px",
          }}
        >
          <Field
            label="Extra EMI Per Year"
            value={extraEmiPerYear}
            onChange={setExtraEmiPerYear}
            placeholder="3"
          />

          <Field
            label="Yearly EMI Increase"
            value={yearlyEmiIncrease}
            onChange={setYearlyEmiIncrease}
            suffix="%"
            step="0.1"
            placeholder="5"
          />
        </div>

        {/* Same Button UI */}
        <CalcBtn
          label="Calculate EMI"
          onClick={calculate}
          color="var(--blue)"
        />
      </Card>

      {result && (
        <>
          <InArticleAd />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
              marginBottom: "14px",
            }}
          >
            <HeroResult
              label="Normal EMI"
              value={fmt(result.normal.emi)}
              color="var(--blue)"
            />

            <HeroResult
              label="Initial EMI"
              value={fmt(result.strategy.initialEMI)}
              color="var(--green)"
            />

            <HeroResult
              label="Final EMI"
              value={fmt(result.strategy.finalEMI)}
              color="var(--purple)"
            />
          </div>

          <StatsGrid
            chips={[
              {
                label: "Normal Total Interest",
                value: fmt(result.normal.totalInterest),
                color: "var(--danger)",
              },
              {
                label: "Strategy Total Interest",
                value: fmt(result.strategy.totalInterest),
                color: "var(--orange)",
              },
              {
                label: "Interest Saved",
                value: fmt(result.interestSaved),
                color: "var(--green)",
              },
              {
                label: "Time Saved",
                value: formatDuration(result.monthsSaved),
                color: "var(--blue)",
              },
            ]}
          />

          <Card style={{ height: "280px", marginBottom: "14px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    {
                      name: "Principal",
                      value: result.strategy.yearlyData.reduce(
                        (sum, item) => sum + item.principal,
                        0,
                      ),
                    },
                    {
                      name: "Interest",
                      value: result.strategy.totalInterest,
                    },
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                >
                  <Cell fill="var(--green)" />
                  <Cell fill="var(--danger)" />
                </Pie>

                <Tooltip
                  formatter={(value) => fmt(value)}
                  contentStyle={{
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    color: "var(--t1)",
                  }}
                />

                <Legend
                  formatter={(value) => (
                    <span
                      style={{
                        color: "var(--t2)",
                        fontSize: "12px",
                      }}
                    >
                      {value}
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Card style={{ marginBottom: "14px" }}>
  <h3
    style={{
      marginBottom: "15px",
      color: "var(--t1)",
    }}
  >
    Loan Comparison
  </h3>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "12px",
    }}
  >
    {/* Normal Loan */}
    <div
      style={{
        padding: "16px",
        borderRadius: "12px",
        background: "var(--bg)",
        border: "1px solid var(--border)",
      }}
    >
      <div
        style={{
          fontSize: "14px",
          fontWeight: 700,
          color: "var(--t1)",
          marginBottom: "14px",
        }}
      >
        Normal Loan
      </div>

      <div style={{ display: "grid", gap: "10px" }}>
        <div>
          <div style={{ fontSize: "11px", color: "var(--t3)" }}>
            Duration
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "var(--t1)",
              marginTop: "2px",
            }}
          >
            {formatDuration(result.normal.months)}
          </div>
        </div>

        <div>
          <div style={{ fontSize: "11px", color: "var(--t3)" }}>
            Total Interest
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "var(--t1)",
              marginTop: "2px",
            }}
          >
            {fmt(result.normal.totalInterest)}
          </div>
        </div>

        <div>
          <div style={{ fontSize: "11px", color: "var(--t3)" }}>
            Total Payment
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "var(--t1)",
              marginTop: "2px",
            }}
          >
            {fmt(result.normal.totalPayment)}
          </div>
        </div>
      </div>
    </div>

    {/* With Strategy */}
    <div
      style={{
        padding: "16px",
        borderRadius: "12px",
        background: "rgba(34, 197, 94, 0.06)",
        border: "1px solid rgba(34, 197, 94, 0.25)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
        }}
      >
        <div
          style={{
            fontSize: "14px",
            fontWeight: 700,
            color: "var(--t1)",
          }}
        >
          With Strategy
        </div>

        <span
          style={{
            fontSize: "10px",
            fontWeight: 700,
            padding: "4px 8px",
            borderRadius: "20px",
            background: "rgba(34, 197, 94, 0.12)",
            color: "var(--green)",
          }}
        >
          SAVINGS
        </span>
      </div>

      <div style={{ display: "grid", gap: "10px" }}>
        <div>
          <div style={{ fontSize: "11px", color: "var(--t3)" }}>
            Duration
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "var(--green)",
              marginTop: "2px",
            }}
          >
            {formatDuration(result.strategy.months)}
          </div>
        </div>

        <div>
          <div style={{ fontSize: "11px", color: "var(--t3)" }}>
            Total Interest
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "var(--green)",
              marginTop: "2px",
            }}
          >
            {fmt(result.strategy.totalInterest)}
          </div>
        </div>

        <div>
          <div style={{ fontSize: "11px", color: "var(--t3)" }}>
            Total Payment
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "var(--green)",
              marginTop: "2px",
            }}
          >
            {fmt(result.strategy.totalPayment)}
          </div>
        </div>
      </div>
    </div>
  </div>
</Card>

          <DataTable
            headers={[
              "Year",
              "EMI",
              "Interest",
              "Principal",
              "Extra Payment",
              "Total Payment",
              "Closing Balance",
            ]}
            rows={result.strategy.yearlyData.map((data) => [
              data.year,
              fmt(data.emi),
              fmt(data.interest),
              fmt(data.principal),
              fmt(data.extraPayment),
              fmt(data.payment),
              fmt(data.closingBalance),
            ])}
          />

          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SIP CALCULATOR
// ─────────────────────────────────────────────────────────────
export function SIPCalculator() {
  const [m, setM] = useState('5000');
  const [r, setR] = useState('12');
  const [y, setY] = useState('10');
  const [res, setRes] = useState(null);

  const calc = () => {
    const P = +m, rate = +r / 12 / 100, n = +y * 12;
    if (!P || !rate) return;
    const fv = P * (Math.pow(1 + rate, n) - 1) / rate * (1 + rate);
    const inv = P * n;
    const chartData = Array.from({ length: +y }, (_, i) => {
      const mn = (i + 1) * 12;
      return { year: `Yr ${i + 1}`, invested: Math.round(P * mn), value: Math.round(P * (Math.pow(1 + rate, mn) - 1) / rate * (1 + rate)) };
    });
    setRes({ fv, inv, gains: fv - inv, chartData });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <Field label="Monthly SIP" value={m} onChange={setM} prefix="₹" />
          <Field label="Expected Return" value={r} onChange={setR} suffix="% p.a." step="0.5" />
          <Field label="Time Period" value={y} onChange={setY} suffix="Years" />
        </div>
        <CalcBtn label="Calculate SIP Returns" onClick={calc} color="var(--green)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Maturity Value" value={fmt(res.fv)} color="var(--green)" />
          <StatsGrid chips={[
            { label: 'Total Invested', value: fmt(res.inv), color: 'var(--blue)' },
            { label: 'Est. Returns', value: fmt(res.gains), color: 'var(--green)' },
            { label: 'Wealth Gained', value: `${(res.gains / res.inv * 100).toFixed(0)}%`, color: 'var(--warn)' },
          ]} />
          <Card style={{ height: '250px', marginBottom: '14px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={res.chartData}>
                <XAxis dataKey="year" tick={{ fill: 'var(--t3)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => `₹${(v / 100000).toFixed(0)}L`} tick={{ fill: 'var(--t3)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip formatter={v => fmt(v)} contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--t1)' }} />
                <Area type="monotone" dataKey="invested" stackId="1" stroke="var(--blue)" fill="rgba(79,142,247,0.2)" name="Invested" />
                <Area type="monotone" dataKey="value" stackId="0" stroke="var(--green)" fill="rgba(45,212,160,0.15)" name="Value" />
                <Legend formatter={v => <span style={{ color: 'var(--t2)', fontSize: '12px' }}>{v}</span>} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FD CALCULATOR
// ─────────────────────────────────────────────────────────────
export function FDCalculator() {
  const [p, setP] = useState('100000');
  const [r, setR] = useState('7');
  const [y, setY] = useState('3');
  const [comp, setComp] = useState('4');
  const [res, setRes] = useState(null);

  const calc = () => {
    const P = +p, rate = +r / 100, n = +comp, t = +y;
    if (!P) return;
    const mat = P * Math.pow(1 + rate / n, n * t);
    setRes({ mat, interest: mat - P, principal: P });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <Field label="Principal Amount" value={p} onChange={setP} prefix="₹" />
          <Field label="Interest Rate" value={r} onChange={setR} suffix="% p.a." step="0.1" />
          <Field label="Tenure" value={y} onChange={setY} suffix="Years" />
          <Select label="Compounding" value={comp} onChange={setComp} options={[
            { value: '1', label: 'Yearly' }, { value: '2', label: 'Half-Yearly' },
            { value: '4', label: 'Quarterly' }, { value: '12', label: 'Monthly' },
          ]} />
        </div>
        <CalcBtn label="Calculate FD Returns" onClick={calc} color="var(--green)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Maturity Amount" value={fmt(res.mat)} color="var(--green)" />
          <StatsGrid chips={[
            { label: 'Principal', value: fmt(res.principal), color: 'var(--blue)' },
            { label: 'Interest Earned', value: fmt(res.interest), color: 'var(--green)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// RD CALCULATOR
// ─────────────────────────────────────────────────────────────
export function RDCalculator() {
  const [m, setM] = useState('5000');
  const [r, setR] = useState('7');
  const [n, setN] = useState('24');
  const [res, setRes] = useState(null);

  const calc = () => {
    const P = +m, rate = +r, months = +n;
    if (!P || !rate || !months) return;
    const mat = P * months + P * months * (months + 1) * (rate / 100) / 24;
    setRes({ mat, inv: P * months, interest: mat - P * months });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <Field label="Monthly Deposit" value={m} onChange={setM} prefix="₹" />
          <Field label="Interest Rate" value={r} onChange={setR} suffix="% p.a." step="0.1" />
          <Field label="Duration" value={n} onChange={setN} suffix="Months" />
        </div>
        <CalcBtn label="Calculate RD Returns" onClick={calc} color="var(--green)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Maturity Amount" value={fmt(res.mat)} color="var(--green)" />
          <StatsGrid chips={[
            { label: 'Total Deposited', value: fmt(res.inv), color: 'var(--blue)' },
            { label: 'Interest Earned', value: fmt(res.interest), color: 'var(--green)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PPF CALCULATOR
// ─────────────────────────────────────────────────────────────
export function PPFCalculator() {
  const [y, setY] = useState('150000');
  const [r, setR] = useState('7.1');
  const [t, setT] = useState('15');
  const [res, setRes] = useState(null);

  const calc = () => {
    const P = +y, rate = +r / 100, years = +t;
    if (!P) return;
    let bal = 0;
    for (let i = 0; i < years; i++) bal = (bal + P) * (1 + rate);
    setRes({ mat: bal, inv: P * years, interest: bal - P * years });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <Field label="Yearly Investment (max ₹1.5L)" value={y} onChange={setY} prefix="₹" />
          <Field label="PPF Rate" value={r} onChange={setR} suffix="% p.a." step="0.05" />
          <Field label="Tenure (min 15 yrs)" value={t} onChange={setT} suffix="Years" />
        </div>
        <CalcBtn label="Calculate PPF Corpus" onClick={calc} color="var(--green)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Maturity Amount" value={fmt(res.mat)} color="var(--green)" />
          <StatsGrid chips={[
            { label: 'Total Invested', value: fmt(res.inv), color: 'var(--blue)' },
            { label: 'Interest Earned', value: fmt(res.interest), color: 'var(--green)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// GST CALCULATOR
// ─────────────────────────────────────────────────────────────
export function GSTCalculator() {
  const [amt, setAmt] = useState('10000');
  const [rate, setRate] = useState('18');
  const [type, setType] = useState('exclusive');
  const [res, setRes] = useState(null);

  const calc = () => {
    const A = +amt, r = +rate / 100;
    if (!A) return;
    let orig, gst, total;
    if (type === 'exclusive') { orig = A; gst = A * r; total = A + gst; }
    else { orig = A / (1 + r); gst = A - orig; total = A; }
    setRes({ orig, gst, total, cgst: gst / 2, igst: gst, rate });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <Field label="Amount" value={amt} onChange={setAmt} prefix="₹" />
          <Select label="GST Rate" value={rate} onChange={setRate} options={['0', '5', '12', '18', '28'].map(r => ({ value: r, label: `${r}%` }))} />
          <Select label="Calculation Type" value={type} onChange={setType} options={[
            { value: 'exclusive', label: 'GST Exclusive — Add GST to amount' },
            { value: 'inclusive', label: 'GST Inclusive — Remove GST from amount' },
          ]} />
        </div>
        <CalcBtn label="Calculate GST" onClick={calc} color="var(--orange)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Total Amount" value={fmt(res.total)} color="var(--orange)" />
          <StatsGrid chips={[
            { label: 'Original Amount', value: fmt(res.orig), color: 'var(--blue)' },
            { label: 'GST Amount', value: fmt(res.gst), color: 'var(--danger)' },
            { label: `CGST (${res.rate / 2}%)`, value: fmt(res.cgst), color: 'var(--purple)' },
            { label: `SGST (${res.rate / 2}%)`, value: fmt(res.cgst), color: 'var(--purple)' },
            { label: `IGST (${res.rate}%)`, value: fmt(res.igst), color: 'var(--warn)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// INCOME TAX
// ─────────────────────────────────────────────────────────────
export function IncomeTaxCalculator() {
  const [inc, setInc] = useState('1000000');
  const [c80c, setC80c] = useState('150000');
  const [c80d, setC80d] = useState('25000');
  const [hra, setHra] = useState('0');
  const [res, setRes] = useState(null);

  const calcNew = (I) => {
    const s = [[0, 300000, 0], [300000, 700000, 5], [700000, 1000000, 10], [1000000, 1200000, 15], [1200000, 1500000, 20], [1500000, Infinity, 30]];
    let t = 0; for (const [a, b, r] of s) if (I > a) t += (Math.min(I, b) - a) * r / 100;
    if (I <= 700000) t = 0;
    return t + t * 0.04;
  };

  const calcOld = (I, ded) => {
    const ti = Math.max(0, I - ded);
    const s = [[0, 250000, 0], [250000, 500000, 5], [500000, 1000000, 20], [1000000, Infinity, 30]];
    let t = 0; for (const [a, b, r] of s) if (ti > a) t += (Math.min(ti, b) - a) * r / 100;
    if (ti <= 500000) t = Math.min(t, 12500);
    return t + t * 0.04;
  };

  const calc = () => {
    const I = +inc, ded = +c80c + +c80d + +hra + 50000;
    const nt = calcNew(I), ot = calcOld(I, ded);
    setRes({ nt, ot, rec: nt < ot ? 'New Regime' : 'Old Regime', save: Math.abs(ot - nt) });
  };

  return (
    <div>
      <Card>
        <Field label="Annual Income" value={inc} onChange={setInc} prefix="₹" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <Field label="80C Deductions" value={c80c} onChange={setC80c} prefix="₹" />
          <Field label="80D Health Insurance" value={c80d} onChange={setC80d} prefix="₹" />
          <Field label="HRA Exemption" value={hra} onChange={setHra} prefix="₹" />
        </div>
        <CalcBtn label="Calculate Income Tax" onClick={calc} color="var(--orange)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <div style={{ background: 'rgba(45,212,160,0.1)', border: '1px solid rgba(45,212,160,0.3)', borderRadius: 'var(--radius)', padding: '18px', textAlign: 'center', marginBottom: '14px' }} className="fade-up">
            <div style={{ fontSize: '12px', color: 'var(--t2)', marginBottom: '4px' }}>💡 Recommended Regime</div>
            <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--green)' }}>{res.rec}</div>
            <div style={{ fontSize: '13px', color: 'var(--t2)', marginTop: '4px' }}>Save {fmt(res.save)}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }} className="fade-up">
            {[['New Regime', res.nt, 'var(--blue)'], ['Old Regime', res.ot, 'var(--purple)']].map(([n, v, c]) => (
              <Card key={n}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: c, marginBottom: '12px' }}>{n}</div>
                <div style={{ fontSize: '12px', color: 'var(--t2)', marginBottom: '4px' }}>Total Tax Payable</div>
                <div style={{ fontSize: '22px', fontWeight: 800, color: c, fontFamily: 'JetBrains Mono, monospace' }}>{fmt(v)}</div>
              </Card>
            ))}
          </div>
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// HRA CALCULATOR
// ─────────────────────────────────────────────────────────────
export function HRACalculator() {
  const [basic, setBasic] = useState('50000');
  const [hraRec, setHraRec] = useState('20000');
  const [rent, setRent] = useState('15000');
  const [city, setCity] = useState('metro');
  const [res, setRes] = useState(null);

  const calc = () => {
    const B = +basic * 12, H = +hraRec * 12, R = +rent * 12;
    if (!B) return;
    const pct = city === 'metro' ? 0.5 : 0.4;
    const a = H, b = Math.max(0, R - 0.1 * B), c = pct * B;
    const ex = Math.min(a, b, c);
    setRes({ hra: H, exempt: ex, taxable: H - ex, a, b, c });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <Field label="Basic Salary / Month" value={basic} onChange={setBasic} prefix="₹" />
          <Field label="HRA Received / Month" value={hraRec} onChange={setHraRec} prefix="₹" />
          <Field label="Rent Paid / Month" value={rent} onChange={setRent} prefix="₹" />
          <Select label="City Type" value={city} onChange={setCity} options={[
            { value: 'metro', label: 'Metro City (Mumbai/Delhi/Kolkata/Chennai) – 50%' },
            { value: 'non-metro', label: 'Non-Metro City – 40%' },
          ]} />
        </div>
        <CalcBtn label="Calculate HRA Exemption" onClick={calc} color="var(--orange)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Annual HRA Exemption" value={fmt(res.exempt)} color="var(--green)" />
          <StatsGrid chips={[
            { label: 'HRA Received', value: fmt(res.hra), color: 'var(--blue)' },
            { label: 'HRA Exempt', value: fmt(res.exempt), color: 'var(--green)' },
            { label: 'Taxable HRA', value: fmt(res.taxable), color: 'var(--danger)' },
          ]} />
          <InfoBox text={`Exemption = Minimum of: Actual HRA (${fmt(res.a)}) | Rent − 10% Basic (${fmt(res.b)}) | ${city === 'metro' ? '50' : '40'}% of Basic (${fmt(res.c)})`} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// EPF CALCULATOR
// ─────────────────────────────────────────────────────────────
export function EPFCalculator() {
  const [basic, setBasic] = useState('25000');
  const [age, setAge] = useState('30');
  const [rate, setRate] = useState('8.25');
  const [res, setRes] = useState(null);

  const calc = () => {
    const B = +basic, r = +rate / 100, yr = 58 - +age;
    if (!B || yr <= 0) return;
    const mEmp = B * 0.12, mEmpl = B * 0.12, total = mEmp + mEmpl;
    const n = yr * 12, mr = r / 12;
    const corpus = total * (Math.pow(1 + mr, n) - 1) / mr * (1 + mr);
    const inv = total * n;
    setRes({ corpus, inv, interest: corpus - inv, mEmp, mEmpl, yr });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <Field label="Basic + DA / Month" value={basic} onChange={setBasic} prefix="₹" />
          <Field label="Current Age" value={age} onChange={setAge} />
          <Field label="EPF Interest Rate" value={rate} onChange={setRate} suffix="% p.a." step="0.05" />
        </div>
        <CalcBtn label="Calculate EPF Corpus" onClick={calc} color="var(--green)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label={`EPF Corpus at Age 58 (${res.yr} years)`} value={fmt(res.corpus)} color="var(--green)" />
          <StatsGrid chips={[
            { label: 'Your Monthly', value: fmt(res.mEmp), color: 'var(--blue)' },
            { label: 'Employer Monthly', value: fmt(res.mEmpl), color: 'var(--purple)' },
            { label: 'Total Invested', value: fmt(res.inv), color: 'var(--warn)' },
            { label: 'Interest Earned', value: fmt(res.interest), color: 'var(--green)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TDS CALCULATOR
// ─────────────────────────────────────────────────────────────
const TDS_SECTIONS = [
  { code: '194A', desc: 'Interest – Banks/FDs', rate: 10, threshold: 40000 },
  { code: '194C', desc: 'Contractor/Sub-contractor', rate: 1, threshold: 30000 },
  { code: '194H', desc: 'Commission / Brokerage', rate: 5, threshold: 15000 },
  { code: '194I', desc: 'Rent – Land & Building', rate: 10, threshold: 240000 },
  { code: '194J', desc: 'Professional/Technical Fees', rate: 10, threshold: 30000 },
  { code: '194B', desc: 'Lottery / Prizes', rate: 30, threshold: 10000 },
  { code: '194D', desc: 'Insurance Commission', rate: 5, threshold: 15000 },
];

export function TDSCalculator() {
  const [amt, setAmt] = useState('100000');
  const [sec, setSec] = useState('194A');
  const [pan, setPan] = useState('yes');
  const [res, setRes] = useState(null);

  const calc = () => {
    const A = +amt;
    const s = TDS_SECTIONS.find(x => x.code === sec);
    let rate = s.rate;
    if (pan === 'no' && rate < 20) rate = 20;
    const tds = A * rate / 100;
    setRes({ tds, net: A - tds, rate, threshold: s.threshold, eligible: A > s.threshold });
  };

  return (
    <div>
      <Card>
        <Field label="Payment Amount" value={amt} onChange={setAmt} prefix="₹" />
        <Select label="TDS Section" value={sec} onChange={setSec} options={TDS_SECTIONS.map(s => ({ value: s.code, label: `${s.code} – ${s.desc} (${s.rate}%)` }))} />
        <Select label="PAN Available?" value={pan} onChange={setPan} options={[{ value: 'yes', label: 'Yes – Normal TDS Rate' }, { value: 'no', label: 'No – 20% TDS Applies' }]} />
        <CalcBtn label="Calculate TDS" onClick={calc} color="var(--orange)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          {!res.eligible && <InfoBox text={`⚠️ Amount ₹${amt} is below TDS threshold of ₹${res.threshold.toLocaleString('en-IN')}. TDS may not be applicable.`} color="var(--warn)" />}
          <HeroResult label={`TDS Amount (${res.rate}%)`} value={fmt(res.tds)} color="var(--danger)" />
          <StatsGrid chips={[
            { label: 'Gross Amount', value: fmt(+amt), color: 'var(--blue)' },
            { label: 'TDS Deducted', value: fmt(res.tds), color: 'var(--danger)' },
            { label: 'Net Payable', value: fmt(res.net), color: 'var(--green)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LUMPSUM
// ─────────────────────────────────────────────────────────────
export function LumpsumCalculator() {
  const [p, setP] = useState('100000');
  const [r, setR] = useState('12');
  const [y, setY] = useState('10');
  const [res, setRes] = useState(null);

  const calc = () => {
    const P = +p, rate = +r / 100, n = +y;
    if (!P) return;
    const fv = P * Math.pow(1 + rate, n);
    setRes({ fv, principal: P, gains: fv - P });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <Field label="Investment Amount" value={p} onChange={setP} prefix="₹" />
          <Field label="Expected Return" value={r} onChange={setR} suffix="% p.a." step="0.5" />
          <Field label="Time Period" value={y} onChange={setY} suffix="Years" />
        </div>
        <CalcBtn label="Calculate Returns" onClick={calc} color="var(--green)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Maturity Value" value={fmt(res.fv)} color="var(--green)" />
          <StatsGrid chips={[
            { label: 'Invested', value: fmt(res.principal), color: 'var(--blue)' },
            { label: 'Est. Gains', value: fmt(res.gains), color: 'var(--green)' },
            { label: 'Total Return', value: `${(res.gains / res.principal * 100).toFixed(0)}%`, color: 'var(--warn)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STEP-UP SIP
// ─────────────────────────────────────────────────────────────
export function StepUpSIPCalculator() {
  const [m, setM] = useState('5000');
  const [r, setR] = useState('12');
  const [y, setY] = useState('15');
  const [s, setS] = useState('10');
  const [res, setRes] = useState(null);

  const calc = () => {
    const mr = +r / 12 / 100, n = +y, step = +s / 100;
    let corpus = 0, invested = 0, sip = +m;
    for (let yr = 0; yr < n; yr++) {
      for (let mo = 0; mo < 12; mo++) { corpus = (corpus + sip) * (1 + mr); invested += sip; }
      sip *= (1 + step);
    }
    setRes({ corpus, invested, gains: corpus - invested });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <Field label="Initial Monthly SIP" value={m} onChange={setM} prefix="₹" />
          <Field label="Annual Step-Up" value={s} onChange={setS} suffix="%" />
          <Field label="Expected Return" value={r} onChange={setR} suffix="% p.a." step="0.5" />
          <Field label="Time Period" value={y} onChange={setY} suffix="Years" />
        </div>
        <CalcBtn label="Calculate Step-Up SIP" onClick={calc} color="var(--green)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Maturity Value" value={fmt(res.corpus)} color="var(--green)" />
          <StatsGrid chips={[
            { label: 'Total Invested', value: fmt(res.invested), color: 'var(--blue)' },
            { label: 'Est. Returns', value: fmt(res.gains), color: 'var(--green)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SWP
// ─────────────────────────────────────────────────────────────
export function SWPCalculator() {
  const [c, setC] = useState('1000000');
  const [w, setW] = useState('8000');
  const [r, setR] = useState('10');
  const [y, setY] = useState('10');
  const [res, setRes] = useState(null);

  const calc = () => {
    let bal = +c;
    const W = +w, mr = +r / 12 / 100, n = +y * 12;
    if (!bal || !W) return;
    let tot = 0;
    for (let i = 0; i < n; i++) { bal = bal * (1 + mr) - W; tot += W; if (bal <= 0) { bal = 0; break; } }
    setRes({ balance: Math.max(0, bal), withdrawn: tot, corpus: +c });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <Field label="Initial Corpus" value={c} onChange={setC} prefix="₹" />
          <Field label="Monthly Withdrawal" value={w} onChange={setW} prefix="₹" />
          <Field label="Expected Return" value={r} onChange={setR} suffix="% p.a." step="0.5" />
          <Field label="Withdrawal Period" value={y} onChange={setY} suffix="Years" />
        </div>
        <CalcBtn label="Calculate SWP" onClick={calc} color="var(--green)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Remaining Corpus" value={fmt(res.balance)} color="var(--green)" />
          <StatsGrid chips={[
            { label: 'Initial Corpus', value: fmt(res.corpus), color: 'var(--blue)' },
            { label: 'Total Withdrawn', value: fmt(res.withdrawn), color: 'var(--orange)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MF RETURN
// ─────────────────────────────────────────────────────────────
export function MFReturnCalculator() {
  const [inv, setInv] = useState('100000');
  const [cur, setCur] = useState('180000');
  const [y, setY] = useState('5');
  const [res, setRes] = useState(null);

  const calc = () => {
    const I = +inv, C = +cur, n = +y;
    if (!I || !C || !n) return;
    const abs = (C - I) / I * 100;
    const cagr = (Math.pow(C / I, 1 / n) - 1) * 100;
    setRes({ abs, cagr, gains: C - I, current: C });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <Field label="Amount Invested" value={inv} onChange={setInv} prefix="₹" />
          <Field label="Current Value" value={cur} onChange={setCur} prefix="₹" />
          <Field label="Investment Period" value={y} onChange={setY} suffix="Years" step="0.5" />
        </div>
        <CalcBtn label="Calculate Returns" onClick={calc} color="var(--green)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }} className="fade-up">
            <HeroResult label="CAGR" value={`${res.cagr.toFixed(2)}%`} color={res.cagr >= 0 ? 'var(--green)' : 'var(--danger)'} />
            <HeroResult label="Absolute Return" value={`${res.abs.toFixed(2)}%`} color={res.abs >= 0 ? 'var(--green)' : 'var(--danger)'} />
          </div>
          <StatsGrid chips={[
            { label: 'Amount Invested', value: fmt(+inv), color: 'var(--blue)' },
            { label: 'Profit / Loss', value: fmt(res.gains), color: res.gains >= 0 ? 'var(--green)' : 'var(--danger)' },
            { label: 'Current Value', value: fmt(res.current), color: 'var(--orange)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CAPITAL GAIN
// ─────────────────────────────────────────────────────────────
export function CapitalGainCalculator() {
  const [buy, setBuy] = useState('100000');
  const [sell, setSell] = useState('180000');
  const [asset, setAsset] = useState('equity');
  const [holding, setHolding] = useState('long');
  const [res, setRes] = useState(null);

  const calc = () => {
    const gain = +sell - +buy;
    let rate = 0, taxType = '';
    if (asset === 'equity') {
      if (holding === 'short') { rate = 20; taxType = 'STCG (Equity) @ 20%'; }
      else { rate = gain > 125000 ? 12.5 : 0; taxType = 'LTCG (Equity) @ 12.5% — First ₹1.25L exempt'; }
    } else {
      if (holding === 'short') { rate = 30; taxType = 'STCG (Debt) — Taxed as per slab (~30%)'; }
      else { rate = 20; taxType = 'LTCG (Debt) @ 20% with indexation'; }
    }
    const tg = (asset === 'equity' && holding === 'long') ? Math.max(0, gain - 125000) : gain;
    const tax = Math.max(0, tg * rate / 100);
    setRes({ gain, tax, netGain: gain - tax, taxableGain: tg, taxType, rate });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <Field label="Purchase Price" value={buy} onChange={setBuy} prefix="₹" />
          <Field label="Sale Price" value={sell} onChange={setSell} prefix="₹" />
          <Select label="Asset Type" value={asset} onChange={setAsset} options={[
            { value: 'equity', label: 'Equity / Mutual Fund' },
            { value: 'debt', label: 'Debt / Property / Others' },
          ]} />
          <Select label="Holding Period" value={holding} onChange={setHolding} options={[
            { value: 'short', label: 'Short Term (STCG)' },
            { value: 'long', label: 'Long Term (LTCG)' },
          ]} />
        </div>
        <CalcBtn label="Calculate Capital Gain Tax" onClick={calc} color="var(--orange)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <InfoBox text={`📌 Tax applicable: ${res.taxType}`} color="var(--blue)" />
          <HeroResult label="Tax Payable" value={fmt(res.tax)} color="var(--danger)" />
          <StatsGrid chips={[
            { label: 'Capital Gain', value: fmt(res.gain), color: res.gain >= 0 ? 'var(--green)' : 'var(--danger)' },
            { label: 'Taxable Gain', value: fmt(res.taxableGain), color: 'var(--warn)' },
            { label: 'Tax Payable', value: fmt(res.tax), color: 'var(--danger)' },
            { label: 'Net Gain', value: fmt(res.netGain), color: 'var(--green)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SSY
// ─────────────────────────────────────────────────────────────
export function SSYCalculator() {
  const [y, setY] = useState('50000');
  const [age, setAge] = useState('5');
  const [r, setR] = useState('8.2');
  const [res, setRes] = useState(null);

  const calc = () => {
    const P = +y, rate = +r / 100, a = +age;
    if (!P || a >= 21) return;
    const totalYears = 21 - a;
    let bal = 0;
    for (let i = 1; i <= totalYears; i++) {
      if (i <= 15) bal = (bal + P) * (1 + rate);
      else bal = bal * (1 + rate);
    }
    setRes({ mat: bal, inv: P * 15, interest: bal - P * 15, years: totalYears });
  };

  return (
    <div>
      <InfoBox text="👧 Sukanya Samriddhi Yojana – Government scheme for girl child. Deposit for 15 years, matures at age 21. Max ₹1.5L/year. Fully tax-free." color="var(--orange)" />
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <Field label="Annual Deposit (max ₹1.5L)" value={y} onChange={setY} prefix="₹" />
          <Field label="Girl's Current Age" value={age} onChange={setAge} />
          <Field label="SSY Rate" value={r} onChange={setR} suffix="% p.a." step="0.05" />
        </div>
        <CalcBtn label="Calculate SSY Returns" onClick={calc} color="var(--green)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label={`Maturity at Age 21 (${res.years} years)`} value={fmt(res.mat)} color="var(--green)" />
          <StatsGrid chips={[
            { label: 'Total Invested', value: fmt(res.inv), color: 'var(--blue)' },
            { label: 'Interest Earned', value: fmt(res.interest), color: 'var(--green)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PROFIT MARGIN
// ─────────────────────────────────────────────────────────────
export function ProfitMarginCalculator() {
  const [rev, setRev] = useState('500000');
  const [cogs, setCogs] = useState('300000');
  const [opex, setOpex] = useState('80000');
  const [res, setRes] = useState(null);

  const calc = () => {
    const R = +rev, C = +cogs, O = +opex;
    if (!R) return;
    const gp = R - C, op = R - C - O;
    setRes({ gp, op, gm: gp / R * 100, om: op / R * 100 });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <Field label="Revenue / Sales" value={rev} onChange={setRev} prefix="₹" />
          <Field label="Cost of Goods Sold" value={cogs} onChange={setCogs} prefix="₹" />
          <Field label="Operating Expenses" value={opex} onChange={setOpex} prefix="₹" />
        </div>
        <CalcBtn label="Calculate Profit Margin" onClick={calc} color="var(--purple)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Gross Profit" value={fmt(res.gp)} color="var(--green)" />
          <StatsGrid chips={[
            { label: 'Gross Margin', value: `${res.gm.toFixed(2)}%`, color: 'var(--green)' },
            { label: 'Operating Profit', value: fmt(res.op), color: 'var(--blue)' },
            { label: 'Operating Margin', value: `${res.om.toFixed(2)}%`, color: 'var(--purple)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DEPRECIATION
// ─────────────────────────────────────────────────────────────
export function DepreciationCalculator() {
  const [cost, setCost] = useState('500000');
  const [salv, setSalv] = useState('50000');
  const [life, setLife] = useState('5');
  const [method, setMethod] = useState('slm');
  const [rows, setRows] = useState(null);

  const calc = () => {
    const C = +cost, S = +salv, L = +life;
    if (!C || !L) return;
    const sched = [];
    if (method === 'slm') {
      const dep = (C - S) / L; let bv = C;
      for (let i = 1; i <= L; i++) { bv -= dep; sched.push([`Year ${i}`, { value: fmt(dep), color: 'var(--danger)' }, { value: fmt(Math.max(S, bv)), color: 'var(--green)' }]); }
    } else {
      const rate = 1 - Math.pow(S / C, 1 / L); let bv = C;
      for (let i = 1; i <= L; i++) { const dep = bv * rate; bv -= dep; sched.push([`Year ${i}`, { value: fmt(dep), color: 'var(--danger)' }, { value: fmt(Math.max(S, bv)), color: 'var(--green)' }]); }
    }
    setRows(sched);
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <Field label="Asset Cost" value={cost} onChange={setCost} prefix="₹" />
          <Field label="Salvage Value" value={salv} onChange={setSalv} prefix="₹" />
          <Field label="Useful Life" value={life} onChange={setLife} suffix="Years" />
          <Select label="Method" value={method} onChange={setMethod} options={[
            { value: 'slm', label: 'SLM – Straight Line Method' },
            { value: 'wdv', label: 'WDV – Written Down Value' },
          ]} />
        </div>
        <CalcBtn label="Calculate Depreciation" onClick={calc} color="var(--purple)" />
      </Card>
      {rows && (
        <>
          <InArticleAd />
          <DataTable headers={['Year', 'Depreciation', 'Book Value']} rows={rows} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BROKERAGE
// ─────────────────────────────────────────────────────────────
export function BrokerageCalculator() {
  const [bp, setBp] = useState('500');
  const [sp, setSp] = useState('550');
  const [qty, setQty] = useState('100');
  const [seg, setSeg] = useState('delivery');
  const [res, setRes] = useState(null);

  const calc = () => {
    const B = +bp, S = +sp, Q = +qty;
    if (!Q) return;
    const bv = B * Q, sv = S * Q, to = bv + sv;
    const brk = seg === 'delivery' ? Math.min(to * 0.001, 20) * 2 : seg === 'intraday' ? Math.min(to * 0.0003, 20) * 2 : Math.min(to * 0.0001, 20) * 2;
    const stt = seg === 'delivery' ? sv * 0.001 : to * 0.00025;
    const exc = to * 0.0000345, sebi = to * 0.000001, stamp = bv * 0.00015;
    const gst = (brk + exc + sebi) * 0.18;
    const total = brk + stt + exc + sebi + stamp + gst;
    const pnl = sv - bv;
    setRes({ brk, stt, exc, sebi, stamp, gst, total, pnl, net: pnl - total });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <Field label="Buy Price (₹)" value={bp} onChange={setBp} step="0.05" />
          <Field label="Sell Price (₹)" value={sp} onChange={setSp} step="0.05" />
          <Field label="Quantity" value={qty} onChange={setQty} />
          <Select label="Segment" value={seg} onChange={setSeg} options={[
            { value: 'delivery', label: 'Equity Delivery' },
            { value: 'intraday', label: 'Equity Intraday' },
            { value: 'futures', label: 'Futures & Options' },
          ]} />
        </div>
        <CalcBtn label="Calculate Brokerage" onClick={calc} color="var(--purple)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }} className="fade-up">
            <HeroResult label="Gross P&L" value={fmt(res.pnl)} color={res.pnl >= 0 ? 'var(--green)' : 'var(--danger)'} />
            <HeroResult label="Net P&L" value={fmt(res.net)} color={res.net >= 0 ? 'var(--green)' : 'var(--danger)'} />
          </div>
          <Card>
            {[['Brokerage', res.brk], ['STT', res.stt], ['Exchange Charges', res.exc], ['SEBI Charges', res.sebi], ['Stamp Duty', res.stamp], ['GST (18%)', res.gst]].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: 'var(--t2)' }}>{l}</span>
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', fontWeight: 600, color: 'var(--t1)' }}>{fmtD(v)}</span>
              </div>
            ))}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--t1)' }}>Total Charges</span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '14px', fontWeight: 800, color: 'var(--danger)' }}>{fmtD(res.total)}</span>
            </div>
          </Card>
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MARGIN
// ─────────────────────────────────────────────────────────────
export function MarginCalculator() {
  const [tv, setTv] = useState('100000');
  const [mp, setMp] = useState('20');
  const [lv, setLv] = useState('5');
  const [res, setRes] = useState(null);

  const calc = () => {
    const T = +tv, M = +mp / 100, L = +lv;
    if (!T) return;
    setRes({ req: T * M, exp: T * M * L, free: T - T * M });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <Field label="Trade Value" value={tv} onChange={setTv} prefix="₹" />
          <Field label="Margin Required" value={mp} onChange={setMp} suffix="%" />
          <Field label="Leverage" value={lv} onChange={setLv} suffix="x" />
        </div>
        <CalcBtn label="Calculate Margin" onClick={calc} color="var(--purple)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Margin Required" value={fmt(res.req)} color="var(--orange)" />
          <StatsGrid chips={[
            { label: 'Leveraged Exposure', value: fmt(res.exp), color: 'var(--blue)' },
            { label: 'Free Margin', value: fmt(res.free), color: 'var(--green)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SALARY
// ─────────────────────────────────────────────────────────────
export function SalaryCalculator() {
  const [ctc, setCtc] = useState('1200000');
  const [res, setRes] = useState(null);

  const calc = () => {
    const annual = +ctc;
    if (!annual) return;
    const basic = annual * 0.4, hra = basic * 0.5, empPF = basic * 0.12;
    const special = annual - basic - hra - empPF - empPF;
    const gross = basic + hra + special;
    const taxable = gross - empPF - 50000;
    const slabs = [[0, 300000, 0], [300000, 700000, 5], [700000, 1000000, 10], [1000000, 1200000, 15], [1200000, 1500000, 20], [1500000, Infinity, 30]];
    let tax = 0; for (const [a, b, r] of slabs) if (taxable > a) tax += (Math.min(taxable, b) - a) * r / 100;
    if (taxable <= 700000) tax = 0;
    const totalTax = tax + tax * 0.04;
    const inHand = gross - empPF - totalTax;
    setRes({ basic, hra, special, gross, empPF, tax: totalTax, inHand, monthly: inHand / 12 });
  };

  return (
    <div>
      <Card>
        <Field label="Annual CTC (Cost to Company)" value={ctc} onChange={setCtc} prefix="₹" />
        <CalcBtn label="Calculate In-Hand Salary" onClick={calc} color="var(--purple)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }} className="fade-up">
            <HeroResult label="Monthly In-Hand" value={fmt(res.monthly)} color="var(--purple)" />
            <HeroResult label="Annual In-Hand" value={fmt(res.inHand)} color="var(--orange)" />
          </div>
          <Card>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>Annual Breakup</div>
            {[['Basic (40%)', res.basic, 'var(--blue)', false], ['HRA (50% of Basic)', res.hra, 'var(--green)', false], ['Special Allowance', res.special, 'var(--purple)', false], ['Gross Salary', res.gross, 'var(--t1)', true], ['EPF Deduction (−)', -res.empPF, 'var(--danger)', false], ['Income Tax (−)', -res.tax, 'var(--danger)', false], ['Annual In-Hand', res.inHand, 'var(--orange)', true]].map(([l, v, c, b]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '9px', paddingBottom: '9px', borderBottom: '1px solid rgba(37,40,57,0.5)' }}>
                <span style={{ fontSize: '13px', color: b ? 'var(--t1)' : 'var(--t2)', fontWeight: b ? 700 : 400 }}>{l}</span>
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', fontWeight: b ? 800 : 600, color: c }}>{v < 0 ? `– ${fmt(-v)}` : fmt(v)}</span>
              </div>
            ))}
          </Card>
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STOCK AVERAGE
// ─────────────────────────────────────────────────────────────
export function StockAverageCalculator() {
  const [entries, setEntries] = useState([{ price: '500', qty: '100' }, { price: '450', qty: '150' }]);
  const [res, setRes] = useState(null);

  const updateEntry = (i, key, val) => {
    const e = [...entries]; e[i] = { ...e[i], [key]: val }; setEntries(e);
  };
  const addEntry = () => setEntries([...entries, { price: '', qty: '' }]);
  const removeEntry = (i) => setEntries(entries.filter((_, idx) => idx !== i));

  const calc = () => {
    const valid = entries.filter(e => e.price && e.qty);
    if (!valid.length) return;
    const totalInv = valid.reduce((s, e) => s + +e.price * +e.qty, 0);
    const totalQty = valid.reduce((s, e) => s + +e.qty, 0);
    setRes({ avg: totalInv / totalQty, totalInv, totalQty });
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <span style={{ fontWeight: 700, color: 'var(--t1)', fontSize: '15px' }}>Stock Purchases</span>
          <button onClick={addEntry} style={{ background: 'rgba(176,110,243,0.15)', border: '1px solid rgba(176,110,243,0.3)', borderRadius: '8px', padding: '6px 14px', color: 'var(--purple)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>+ Add Row</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 40px', gap: '8px', marginBottom: '8px' }}>
          {['Buy Price (₹)', 'Quantity', ''].map((h, i) => <div key={i} style={{ fontSize: '11px', fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</div>)}
        </div>
        {entries.map((e, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 40px', gap: '8px', marginBottom: '8px' }}>
            <input type="number" value={e.price} onChange={ev => updateEntry(i, 'price', ev.target.value)} placeholder="500" />
            <input type="number" value={e.qty} onChange={ev => updateEntry(i, 'qty', ev.target.value)} placeholder="100" />
            {entries.length > 1 ? (
              <button onClick={() => removeEntry(i)} style={{ background: 'rgba(255,92,122,0.1)', border: '1px solid rgba(255,92,122,0.3)', borderRadius: '8px', color: 'var(--danger)', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>×</button>
            ) : <div />}
          </div>
        ))}
        <CalcBtn label="Calculate Average Price" onClick={calc} color="var(--purple)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Average Buy Price" value={`₹${res.avg.toFixed(2)}`} color="var(--purple)" />
          <StatsGrid chips={[
            { label: 'Total Shares', value: res.totalQty.toLocaleString('en-IN'), color: 'var(--blue)' },
            { label: 'Total Investment', value: fmt(res.totalInv), color: 'var(--orange)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// REDUCING INTEREST RATE
// ─────────────────────────────────────────────────────────────
export function ReducingRateCalculator() {
  const [p, setP] = useState('500000');
  const [e, setE] = useState('10500');
  const [n, setN] = useState('60');
  const [res, setRes] = useState(null);

  const calc = () => {
    const P = +p, E = +e, months = +n;
    if (!P || !E || !months) return;
    let r = 0.01;
    for (let i = 0; i < 1000; i++) {
      const pw = Math.pow(1 + r, months);
      const f = P * r * pw / (pw - 1) - E;
      if (Math.abs(f) < 0.001) break;
      const df = P * (pw * (r * months - months - 1) + months) / Math.pow(pw - 1, 2);
      r = r - f / df;
    }
    setRes({ annual: r * 12 * 100, monthly: r * 100, totalAmt: E * months, totalInt: E * months - P });
  };

  return (
    <div>
      <InfoBox text="💡 Enter your loan amount, monthly EMI, and tenure to find the effective reducing balance interest rate." color="var(--blue)" />
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <Field label="Loan Amount" value={p} onChange={setP} prefix="₹" />
          <Field label="Monthly EMI" value={e} onChange={setE} prefix="₹" />
          <Field label="Tenure" value={n} onChange={setN} suffix="Months" />
        </div>
        <CalcBtn label="Find Interest Rate" onClick={calc} color="var(--blue)" />
      </Card>
      {res && (
        <>
          <InArticleAd />
          <HeroResult label="Effective Annual Interest Rate" value={`${res.annual.toFixed(2)}% p.a.`} color="var(--blue)" />
          <StatsGrid chips={[
            { label: 'Monthly Rate', value: `${res.monthly.toFixed(4)}%`, color: 'var(--blue)' },
            { label: 'Total Interest', value: fmt(res.totalInt), color: 'var(--danger)' },
            { label: 'Total Payment', value: fmt(res.totalAmt), color: 'var(--orange)' },
          ]} />
          <PostResultAd />
        </>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// privacy - policy 
// ─────────────────────────────────────────────────────────────

export function PrivacyPolicy() {
  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '30px 16px' }}>
      <div
      // style={{
      //   background: 'var(--card)',
      //   border: '1px solid var(--border)',
      //   borderRadius: '24px',
      //   padding: '30px',
      // }}
      >
        <div style={{ textAlign: 'left', marginBottom: '30px' }}>
          <p
            style={{
              color: 'var(--t2)',
              fontSize: '14px',
            }}
          >
            Apnaa Calculator values your privacy and is committed to protecting user information.
          </p>
        </div>

        {[
          {
            title: 'Information Collection and Usage',
            desc: `Apnaa Calculator does not collect, store, or share any personal financial information entered by users inside the app.

The app works primarily offline and all calculations remain locally on your device.`
          },
          {
            title: 'No Login Required',
            desc: 'Users can use the app without creating an account or signing in.'
          },
          {
            title: 'Advertisements',
            desc: `Apnaa Calculator uses Google AdMob to display advertisements. AdMob may collect limited device-related information in accordance with Google's Privacy Policy to provide relevant ads and improve advertising services.`
          },
          {
            title: 'Data Sharing',
            desc: 'We do not sell, trade, or share your personal data with third parties.'
          },
          {
            title: 'Permissions Used',
            desc: 'We value user privacy and strive to keep the app secure and reliable.'
          },
          {
            title: 'Security',
            desc: (
              <>
                The app may request permissions such as:
                <br />
                • Notification permission for reminders and alerts
                <br />
                • Exact alarm permission for scheduled calculator reminders
                <br />
                • Internet permission for ads and updates
                <br />
                These permissions are only used for app functionality.
              </>
            )
          },
          {
            title: 'Children',
            desc: 'Apnaa Calculator is not directed toward children under 13 years of age.'
          },
          {
            title: 'Security',
            desc: 'We value user privacy and strive to keep the app secure and reliable.'
          },
          {
            title: 'Changes to This Policy',
            desc: 'This Privacy Policy may be updated from time to time. Updates will be reflected within the app or app listing.'
          },
          {
            title: 'Contact Us',
            desc: 'If you have questions regarding this Privacy Policy, please contact us through the developer contact information provided on the Google Play Store listing.'
          },
        ].map((item, i) => (
          <div
            key={i}
            style={{
              background: 'rgba(255,107,44,0.05)',
              border: '1px solid rgba(255,107,44,0.12)',
              borderRadius: '18px',
              padding: '20px',
              marginBottom: '16px',
            }}
          >
            <h2
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--orange)',
                marginBottom: '10px',
              }}
            >
              {item.title}
            </h2>

            <p
              style={{
                fontSize: '14px',
                color: 'var(--t2)',
                lineHeight: 1.8,
              }}
            >
              {item.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────
// Term- condition
// ─────────────────────────────────────────────────────────────

export function TermsConditions() {
  const sectionStyle = {
    background: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: '24px',
    padding: '24px',
    marginBottom: '18px',
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--orange)',
    marginBottom: '14px',
  };

  const textStyle = {
    fontSize: '14px',
    color: 'var(--t2)',
    lineHeight: 1.8,
  };

  return (
    <div
    // style={{
    //   minHeight: '100vh',
    //   marginTop: '14px',
    //   padding: '40px 16px',
    //   borderRadius: '24px',
    //   background:
    //     'linear-gradient(to bottom, rgba(255,107,44,0.06), transparent)',
    // }}
    >
      <div
        style={{
          maxWidth: '1000px',
          margin: '0 auto',
        }}
      >
        {/* Header */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: '30px',
          }}
        >
        </div>

        {/* Intro */}
        <div style={sectionStyle}>
          <p style={textStyle}>
            By downloading or using Apnaa Calculator, you agree to the
            following terms and conditions.
          </p>
        </div>

        {/* Usage */}
        <div style={sectionStyle}>
          <h2 style={titleStyle}>Usage</h2>

          <p style={{ ...textStyle, marginBottom: '14px' }}>
            Apnaa Calculator is intended for educational, informational, and general financial calculation purposes only.
          </p>

          <p style={{ ...textStyle, marginBottom: '10px' }}>
            The app provides calculators related to:
          </p>

          <div style={{ display: 'grid', gap: '10px' }}>
            {[
              '💰 Loans',
              '📈 Investments',
              '🧾 Taxes',
              '💼 Salary',
              '🏢 Business calculations',
              '📊 Financial planning',
            ].map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  color: 'var(--t2)',
                }}
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Accuracy */}
        <div style={sectionStyle}>
          <h2 style={titleStyle}>Accuracy</h2>

          <p style={textStyle}>
            We aim to provide accurate calculations; however, results generated by the app should not be considered professional financial, tax, legal, or investment advice.
          </p>

          <p style={{ ...textStyle, marginTop: '10px' }}>
            Users are advised to verify important financial calculations independently or consult qualified professionals.
          </p>
        </div>

        {/* User Responsibility */}
        <div style={sectionStyle}>
          <h2 style={titleStyle}>User Responsibility</h2>
          <p style={textStyle}>
            Users are responsible for how they use the calculation results generated by the app.
          </p>
        </div>

        {/* Advertisements */}
        <div style={sectionStyle}>
          <h2 style={titleStyle}>Advertisements</h2>
          <p style={textStyle}>
            The app may display advertisements provided by Google AdMob.
          </p>
        </div>

        {/* Offline Usage */}
        <div style={sectionStyle}>
          <h2 style={titleStyle}>Offline Usage</h2>
          <p style={textStyle}>
            Most features of the app work offline and calculations are processed locally on the device.
          </p>
        </div>

        {/* Restrictions */}
        <div style={sectionStyle}>
          <h2 style={titleStyle}>Restrictions</h2>

          <p style={{ ...textStyle, marginBottom: '10px' }}>
            Users must not:
          </p>
          <div style={{ display: 'grid', gap: '10px' }}>
            {[
              '❌ Modify or reverse engineer the app',
              '🚫 Use the app for unlawful activities',
              '🔒 Attempt unauthorized access to app systems',
            ].map((item, index) => (
              <div
                key={index}
                style={{
                  fontSize: '14px',
                  color: 'var(--t2)',
                }}
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Intellectual Property */}
        <div style={sectionStyle}>
          <h2 style={titleStyle}>Intellectual Property</h2>
          <p style={textStyle}>
            All app content, design, branding, icons, and features belong to Apnaa Calculator and are protected by applicable laws.
          </p>
        </div>

        {/* Termination */}
        <div style={sectionStyle}>
          <h2 style={titleStyle}>Termination</h2>
          <p style={textStyle}>
            We reserve the right to update, modify, or discontinue app features at any time without prior notice.
          </p>
        </div>

        {/* Changes to Terms */}
        <div style={sectionStyle}>
          <h2 style={titleStyle}>Changes to Terms</h2>
          <p style={textStyle}>
            These Terms & Conditions may be updated periodically.
          </p>
        </div>

        {/* Contact */}
        <div style={sectionStyle}>
          <h2 style={titleStyle}>Contact</h2>
          <p style={textStyle}>
            For support or inquiries, users can contact the developer through the Google Play Store contact information.
          </p>
        </div>
      </div>
    </div>
  );
}


export function Disclaimer() {
  const cardStyle = {
    background: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: '24px',
    padding: '24px',
    marginBottom: '18px',
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--t1)',
    marginBottom: '14px',
  };

  const textStyle = {
    fontSize: '14px',
    color: 'var(--t2)',
    lineHeight: 1.8,
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        padding: '40px 16px',
      }}
    >
      <div
        style={{
          maxWidth: '1000px',
          margin: '0 auto',
        }}
      >
        {/* Intro */}
        <div
          style={{
            ...cardStyle,
          }}
        >
          <p style={textStyle}>
            Apnaa Calculator is a financial utility application designed to help users perform calculations related to loans, investments, taxes, salary, business, and financial planning.
          </p>

          <p style={{ ...textStyle, marginTop: '14px' }}>
            The information and calculations provided in the app are for general informational and educational purposes only.
          </p>
        </div>

        {/* Not Provide */}
        <div style={cardStyle}>
          <h2 style={titleStyle}>
            Apnaa Calculator Does Not Provide
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
              gap: '14px',
            }}
          >
            {[
              '💼 Financial advice',
              '📈 Investment advice',
              '🧾 Tax consultation',
              '⚖️ Legal advice',
              '🏦 Banking services',
            ].map((item, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255,92,122,0.08)',
                  border: '1px solid rgba(255,92,122,0.15)',
                  borderRadius: '18px',
                  padding: '16px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--t1)',
                }}
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Expert Consultation */}
        <div
          style={{
            ...cardStyle,
          }}
        >

          <p style={textStyle}>
            Users should consult certified financial advisors, tax professionals, or relevant experts before making financial decisions.
          </p>
        </div>

        {/* Accuracy */}
        <div
          style={{
            ...cardStyle,
          }}
        >

          <p style={textStyle}>
            Calculation results may vary depending on formulas, government policies, taxation rules, interest rates, or financial institution policies.
          </p>

          <p style={{ ...textStyle, marginTop: '12px' }}>
            While we strive to keep calculations accurate and updated, we do not guarantee completeness, reliability, or absolute accuracy of results.
          </p>
        </div>

        {/* User Responsibility */}
        <div
          style={{
            background: 'rgba(255,107,44,0.08)',
            border: '1px solid rgba(255,107,44,0.18)',
            borderRadius: '24px',
            padding: '24px',
            marginBottom: '18px',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'flex-start',
            }}
          >
            <div style={{ fontSize: '32px' }}>📌</div>

            <div>
              <h2
                style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: 'var(--orange)',
                  marginBottom: '10px',
                }}
              >
                User Responsibility
              </h2>

              <p style={textStyle}>
                Users are solely responsible for any financial decisions made based on app calculations.
              </p>
            </div>
          </div>
        </div>

        {/* Ads */}
        <div
          style={{
            ...cardStyle,
          }}
        >
          <h2 style={titleStyle}>Advertisements</h2>

          <p style={textStyle}>
            Google AdMob advertisements displayed inside the app are managed by Google and may be personalized according to Google's advertising policies.
          </p>
        </div>

        {/* Final Warning */}
        <div
          style={{
            background: 'rgba(255,92,122,0.08)',
            border: '1px solid rgba(255,92,122,0.18)',
            borderRadius: '24px',
            padding: '24px',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'flex-start',
            }}
          >
            <div style={{ fontSize: '32px' }}>🚨</div>

            <div>
              <h2
                style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: 'var(--danger)',
                  marginBottom: '10px',
                }}
              >
                Liability Notice
              </h2>

              <p style={textStyle}>
                By using Apnaa Calculator, you agree that the developers shall not be held liable for any financial loss, damages, or decisions resulting from the use of the app.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}